/**
 * deriveClickLabel — человеческая метка клика из ближайшего интерактивного предка.
 *
 * Autocapture-стиль (как PostHog, П21): один делегированный listener на document
 * ловит все клики, метка выводится из DOM. Приоритет: data-track → aria-label →
 * текст → role/tag. Не интерактивный таргет → null (не пишем мусор/шум).
 *
 * Приватность (критично): берём ТОЛЬКО метку кнопки/ссылки (label/text/role),
 * НИКОГДА не `value` полей ввода. INTERACTIVE не включает text/email/password
 * input'ы → их клики не порождают метку (null), значения форм не утекают.
 */

const INTERACTIVE =
  'button, a, [role="button"], [data-track], input[type="button"], input[type="submit"]';

const norm = (s: string): string => s.replace(/\s+/g, ' ').trim();

/**
 * Видимый текст кнопки. Кнопка часто собрана из нескольких дочерних элементов
 * (цена + кредиты), и `textContent` склеивает их без пробела → «10 ₽0.50 кр».
 * Корень (П0): если есть ≥2 дочерних ЭЛЕМЕНТА со своим текстом — собираем по ним
 * с разделителем `· `; иначе обычный `textContent` (простой текст не дробим).
 */
function visibleText(target: Element): string {
  const parts = Array.from(target.children)
    .map((c) => norm(c.textContent ?? ''))
    .filter(Boolean);
  if (parts.length >= 2) return parts.join(' · ');
  return norm(target.textContent ?? '');
}

export function deriveClickLabel(el: Element | null): string | null {
  if (!el) return null;
  const target = el.closest(INTERACTIVE);
  if (!target) return null;
  const dt = target.getAttribute('data-track');
  if (dt) return dt;
  const aria = target.getAttribute('aria-label');
  if (aria) return aria.trim();
  const text = visibleText(target);
  if (text) return text.slice(0, 60);
  return target.getAttribute('role') || target.tagName.toLowerCase();
}
